/**
 * PermissionMatrixEditor — Enterprise RBAC (Feb 2026).
 *
 * Herbruikbare component die de volledige permission-registry rendert als
 * inklapbare module-accordion. Twee use-cases:
 *   1. **Rol-editor:** `selectedPermissions` = Set van keys, klik = toggle
 *   2. **User-override-editor:** iedere permission heeft een 3-state widget
 *      (Erven / Toestaan / Blokkeren) via `renderOverrideControl` prop
 *
 * Extras:
 *   - Zoekfunctie
 *   - Filter op module
 *   - Bulk-acties per submodule (Alleen bekijken / Volledige toegang / Geen)
 *   - Duidelijk markering voor protected/gevoelige rechten
 */
import React, { useMemo, useState } from "react";
import {
  Search, ChevronDown, ChevronRight, Shield, AlertTriangle,
  CheckCircle2, XCircle, Eye,
} from "lucide-react";
import { Input } from "../../ui/input";

const ACTIONS_FULL = ["view", "create", "edit", "send", "download", "export", "approve",
                       "cancel", "delete", "manage", "publish", "archive", "restore",
                       "configure", "import"];
const ACTIONS_VIEW_ONLY = ["view"];

export default function PermissionMatrixEditor({
  registry, selectedPermissions, onChange,
  renderOverrideControl,        // optional: (perm) => JSX (for user overrides)
  disabled = false,
}) {
  const [query, setQuery] = useState("");
  const [expandedModules, setExpandedModules] = useState(
    () => new Set(registry.modules.filter((m) => m.key !== "beheer").map((m) => m.key)),
  );

  const toggleModule = (k) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const q = query.trim().toLowerCase();

  const filteredModules = useMemo(() => {
    if (!q) return registry.modules;
    return registry.modules
      .map((m) => {
        const subs = m.submodules
          .map((sm) => {
            const perms = sm.permissions.filter(
              (p) => p.label.toLowerCase().includes(q)
                || p.key.toLowerCase().includes(q)
                || p.description?.toLowerCase().includes(q),
            );
            return perms.length ? { ...sm, permissions: perms } : null;
          })
          .filter(Boolean);
        return subs.length ? { ...m, submodules: subs } : null;
      })
      .filter(Boolean);
  }, [registry, q]);

  // Toggle helper (voor rol-editor)
  const togglePerm = (key) => {
    if (disabled || !onChange) return;
    const next = new Set(selectedPermissions);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
  };

  const bulkSet = (perms, action) => {
    if (disabled || !onChange) return;
    const next = new Set(selectedPermissions);
    if (action === "all") {
      perms.forEach((p) => next.add(p.key));
    } else if (action === "view") {
      perms.forEach((p) => {
        if (p.action === "view") next.add(p.key); else next.delete(p.key);
      });
    } else if (action === "none") {
      perms.forEach((p) => next.delete(p.key));
    }
    onChange(next);
  };

  return (
    <div className="border border-slate-200 rounded" data-testid="permission-matrix-editor">
      <div className="p-2 border-b border-slate-100 bg-slate-50">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek permissie…"
            className="pl-7 text-xs h-8"
            data-testid="permission-matrix-search"
          />
        </div>
      </div>

      <div className="max-h-[520px] overflow-y-auto">
        {filteredModules.length === 0 && (
          <div className="p-6 text-center text-xs text-slate-500 italic">
            Geen permissies gevonden voor "{query}".
          </div>
        )}
        {filteredModules.map((m) => {
          const expanded = expandedModules.has(m.key);
          const totalPerms = m.submodules.reduce((sum, sm) => sum + sm.permissions.length, 0);
          const selectedInModule = m.submodules.reduce(
            (sum, sm) => sum + sm.permissions.filter((p) => selectedPermissions.has(p.key)).length,
            0,
          );
          return (
            <div key={m.key} className="border-b border-slate-100 last:border-b-0"
                 data-testid={`perm-module-${m.key}`}>
              <button
                type="button"
                onClick={() => toggleModule(m.key)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                data-testid={`perm-module-toggle-${m.key}`}
              >
                {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                <span className="font-semibold text-sm text-brand-900">{m.label}</span>
                <span className="text-[10px] text-slate-500 tabular-nums ml-auto">
                  {renderOverrideControl ? `${totalPerms} rechten` : `${selectedInModule}/${totalPerms}`}
                </span>
              </button>

              {expanded && (
                <div className="pl-6 pr-3 pb-2 space-y-2">
                  {m.submodules.map((sm) => (
                    <div key={sm.key} className="border-l border-slate-200 pl-3 py-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">
                          {sm.label}
                        </span>
                        {!renderOverrideControl && !disabled && (
                          <div className="ml-auto flex gap-1">
                            <button type="button" onClick={() => bulkSet(sm.permissions, "all")}
                                    className="text-[9px] px-1.5 py-0.5 border border-slate-200 rounded hover:bg-emerald-50 hover:border-emerald-300"
                                    data-testid={`perm-bulk-all-${sm.key}`}>
                              Alles
                            </button>
                            <button type="button" onClick={() => bulkSet(sm.permissions, "view")}
                                    className="text-[9px] px-1.5 py-0.5 border border-slate-200 rounded hover:bg-blue-50 hover:border-blue-300"
                                    data-testid={`perm-bulk-view-${sm.key}`}>
                              Alleen bekijken
                            </button>
                            <button type="button" onClick={() => bulkSet(sm.permissions, "none")}
                                    className="text-[9px] px-1.5 py-0.5 border border-slate-200 rounded hover:bg-red-50 hover:border-red-300"
                                    data-testid={`perm-bulk-none-${sm.key}`}>
                              Geen
                            </button>
                          </div>
                        )}
                      </div>
                      <ul className="space-y-0.5">
                        {sm.permissions.map((p) => (
                          <PermRow
                            key={p.key}
                            perm={p}
                            selected={selectedPermissions.has(p.key)}
                            onToggle={() => togglePerm(p.key)}
                            renderOverrideControl={renderOverrideControl}
                            disabled={disabled}
                          />
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PermRow({ perm, selected, onToggle, renderOverrideControl, disabled }) {
  if (renderOverrideControl) {
    return (
      <li className="flex items-center gap-2 py-1" data-testid={`perm-row-${perm.key}`}>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-brand-900 truncate flex items-center gap-1">
            {perm.protected && (
              <Shield className="w-3 h-3 text-amber-500" title="Beschermde permissie" />
            )}
            {perm.label}
          </div>
          <div className="text-[10px] text-slate-400 font-mono truncate">{perm.key}</div>
        </div>
        {renderOverrideControl(perm)}
      </li>
    );
  }
  return (
    <li className="flex items-center gap-2 py-0.5" data-testid={`perm-row-${perm.key}`}>
      <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
        <input
          type="checkbox"
          checked={selected}
          disabled={disabled}
          onChange={onToggle}
          data-testid={`perm-checkbox-${perm.key}`}
        />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-brand-900 truncate flex items-center gap-1">
            {perm.protected && <Shield className="w-3 h-3 text-amber-500" title="Beschermde permissie" />}
            {perm.label}
            <span className="text-[9px] px-1 py-0 bg-slate-100 text-slate-500 rounded">{perm.action_label}</span>
          </div>
          <div className="text-[10px] text-slate-400 font-mono truncate">{perm.key}</div>
        </div>
      </label>
    </li>
  );
}
